/**
 * МОСТ К ВЫБОРУ ФАЙЛА В MAC-ОБОЛОЧКЕ (Electron), 2026-09-24.
 *
 * ЗАЧЕМ. Замер Антона: в Mac-приложении нажатие на «плюс» и на любой
 * аватар мгновенно даёт «файл не выбран», Finder не открывается вообще.
 * Тот же веб в браузере работает. Значит ломается не веб, а путь
 * `<input type="file">` внутри оболочки.
 *
 * Оболочка (репозиторий alg-messenger-desktop, ветка
 * momo/desktop-filepicker-20260924, версия 0.1.8) открывает системное окно
 * САМА, в main-процессе, через `dialog.showOpenDialog`, и выставляет в
 * renderer мост `window.algShell`. Путей на диске мост не отдаёт: вместо
 * пути — одноразовый токен, байты забираются отдельным вызовом.
 *
 * ЧТО ЗДЕСЬ. Тонкая обёртка над этим контрактом, превращающая результат в
 * обычные `File`, — дальше весь код работает как с штатным input и ничего
 * про оболочку не знает.
 *
 * ГРАНИЦА ОТВЕТСТВЕННОСТИ. В браузере `window.algShell` нет, `isShellFilePickerAvailable()`
 * возвращает false, и ни одна строка отсюда не выполняется: браузерное
 * поведение остаётся прежним.
 *
 * НЕ ПРОВЕРЕНО ОТСЮДА: запустить macOS-приложение с этой машины (Linux)
 * невозможно. Живая проверка — только у Антона на Mac.
 */

/** Наборы фильтров, которые понимает оболочка (main.js, setupFilePicker). */
export type ShellPickKind =
  | "avatar"
  | "image"
  | "video"
  | "media"
  | "audio"
  | "document"
  | "file";

/** Метаданные выбранного файла. Пути на диске мост не отдаёт принципиально. */
type ShellPickedMeta = {
  token: string;
  name: string;
  size: number;
  mime: string;
  lastModified: number;
};

type ShellPickResult =
  | { ok: true; canceled: boolean; files: ShellPickedMeta[] }
  | { ok: false; error: string };

type ShellReadResult =
  | {
      ok: true;
      name: string;
      mime: string;
      size: number;
      lastModified: number;
      bytes: Uint8Array;
    }
  | { ok: false; error: string; size?: number; maxBytes?: number; message?: string };

type ShellBridge = {
  fileApiVersion?: number;
  pickFiles: (opts: {
    kind: ShellPickKind;
    multiple: boolean;
    title?: string;
  }) => Promise<ShellPickResult>;
  readPickedFile: (token: string) => Promise<ShellReadResult>;
  releasePickedFile: (token: string) => Promise<unknown>;
};

declare global {
  interface Window {
    algShell?: Partial<ShellBridge>;
  }
}

/**
 * Причина отказа в терминах, понятных интерфейсу. Строки протокола
 * оболочки («unreadable», «ipc-failed», …) наружу не выходят: место
 * вызова переводит именно эти три случая.
 */
export type ShellPickFailureCode = "too-large" | "failed";

/** Ошибка моста. Для `too-large` несёт размер файла и предел. */
export class ShellPickFailure extends Error {
  readonly code: ShellPickFailureCode;
  readonly fileName?: string;
  readonly size?: number;
  readonly maxBytes?: number;

  constructor(
    code: ShellPickFailureCode,
    detail: string,
    extra?: { fileName?: string; size?: number; maxBytes?: number },
  ) {
    super(detail);
    this.name = "ShellPickFailure";
    this.code = code;
    this.fileName = extra?.fileName;
    this.size = extra?.size;
    this.maxBytes = extra?.maxBytes;
  }
}

/**
 * Есть ли у нас оболочка с рабочим выбором файла.
 *
 * Проверяем НАЛИЧИЕ ФУНКЦИЙ, а не просто объекта: старая версия оболочки
 * (до 0.1.8) выставляет `window.algShell` без файлового моста, и уйти по
 * этой ветке там означало бы сломать выбор файла совсем.
 */
export function isShellFilePickerAvailable(): boolean {
  if (typeof window === "undefined") return false;
  const bridge = window.algShell;
  return (
    typeof bridge?.pickFiles === "function" &&
    typeof bridge?.readPickedFile === "function"
  );
}

/**
 * Открыть системное окно выбора файла силами оболочки и вернуть обычные `File`.
 *
 * @returns `null`, если человек закрыл окно, ничего не выбрав (это не ошибка
 *          и говорить о ней не нужно), иначе непустой список файлов.
 * @throws  {ShellPickFailure} `too-large` — файл больше предела оболочки;
 *          `failed` — всё остальное (мост отказал, файл не прочитался).
 */
export async function pickFilesViaShell(opts: {
  kind: ShellPickKind;
  multiple: boolean;
  title?: string;
}): Promise<File[] | null> {
  const bridge = window.algShell as ShellBridge | undefined;
  if (!bridge?.pickFiles || !bridge.readPickedFile) {
    throw new ShellPickFailure("failed", "shell bridge missing");
  }

  let picked: ShellPickResult;
  try {
    picked = await bridge.pickFiles({
      kind: opts.kind,
      multiple: opts.multiple,
      title: opts.title,
    });
  } catch (err) {
    throw new ShellPickFailure("failed", (err as Error)?.message ?? "pickFiles threw");
  }

  if (!picked.ok) throw new ShellPickFailure("failed", picked.error);
  if (picked.canceled || picked.files.length === 0) return null;

  const files: File[] = [];
  for (const meta of picked.files) {
    let read: ShellReadResult;
    try {
      read = await bridge.readPickedFile(meta.token);
    } catch (err) {
      await releaseQuietly(bridge, picked.files);
      throw new ShellPickFailure("failed", (err as Error)?.message ?? "readPickedFile threw", {
        fileName: meta.name,
      });
    }

    if (!read.ok) {
      // Токены отпускаем ДО выброса: иначе выбранное висит в памяти
      // main-процесса до конца сеанса.
      await releaseQuietly(bridge, picked.files);
      if (read.error === "too-large") {
        throw new ShellPickFailure("too-large", read.message ?? "file too large", {
          fileName: meta.name,
          size: read.size ?? meta.size,
          maxBytes: read.maxBytes,
        });
      }
      throw new ShellPickFailure("failed", read.error, { fileName: meta.name });
    }

    // Копия в собственный ArrayBuffer: `bytes` приходит через structured
    // clone из main-процесса, и держать `File` поверх чужого буфера —
    // лишняя связь. BlobPart требует именно ArrayBuffer/ArrayBufferView.
    const bytes = new Uint8Array(read.bytes);
    files.push(
      new File([bytes], read.name || meta.name, {
        type: read.mime || meta.mime || "",
        lastModified: read.lastModified || meta.lastModified || Date.now(),
      }),
    );
  }

  // File собран, содержимое скопировано — токены оболочке больше не нужны.
  await releaseQuietly(bridge, picked.files);
  return files;
}

/**
 * Отпустить токены. Сбой освобождения НИКОГДА не должен ломать выбор
 * файла: человек своё уже выбрал, а утечка токена ограничена таблицей
 * на 200 записей в оболочке.
 */
async function releaseQuietly(bridge: ShellBridge, metas: ShellPickedMeta[]): Promise<void> {
  if (typeof bridge.releasePickedFile !== "function") return;
  for (const meta of metas) {
    try {
      await bridge.releasePickedFile(meta.token);
    } catch {
      /* намеренно тихо */
    }
  }
}
